import { convexQuery } from "@convex-dev/react-query";
import { formatDate } from "@plasma/utils/time";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import {
  locationSalesGetProjections,
  type MerchantProjectionEntry,
} from "~/api-refs";
import { Button } from "~/components/ui/button";
import { toConvexTableId } from "~/lib/convex-table-id";
import { enumIncludes, parseEnumValue } from "~/lib/parse-enum-value";

import { SalesDataTable } from "../sales-data-table";

type TimeFrame = "DAILY" | "WEEKLY" | "MONTHLY";
type ProjectionData = MerchantProjectionEntry;

const TIME_FRAMES = ["DAILY", "WEEKLY", "MONTHLY"] as const;
const isTimeFrame = enumIncludes(TIME_FRAMES);

const timeFrameOptions: { value: TimeFrame; label: string }[] = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
];

const OrderTypeBreakdown = ({
  breakdown,
}: {
  breakdown: NonNullable<ProjectionData["orderTypeBreakdown"]>;
}) => {
  return (
    <div className="px-4 py-3">
      <h4 className="label text-foreground mb-3">Order Type Breakdown</h4>
      <div className="grid grid-cols-4 gap-3">
        {Object.entries(breakdown).map(([type, data]) => (
          <div
            className="border-border bg-background rounded-lg border p-3 shadow-xs"
            key={type}
          >
            <div className="label text-foreground capitalize">
              {type.replace("-", " ")}
            </div>
            <div className="mt-1.5 space-y-1">
              <div className="body-sm text-muted-foreground">
                Orders:{" "}
                <span className="label text-foreground">
                  {data.count.toLocaleString()}
                </span>
              </div>
              <div className="body-sm text-muted-foreground">
                Amount:{" "}
                <span className="label text-foreground">
                  {data.amount.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface SalesProjectionsViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const SalesProjectionsView = ({
  locationId,
  dateRange,
  tableId,
}: SalesProjectionsViewProps) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [timeFrame, setTimeFrame] = useState<TimeFrame>("DAILY");

  const handleToggleExpand = useCallback((id: string) => {
    setExpandedRows((old) => ({
      ...old,
      [id]: !old[id],
    }));
  }, []);

  const { data: getProjections } = useQuery(
    convexQuery(locationSalesGetProjections, {
      timeFrame,
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );
  const projections =
    getProjections?.map((p) => ({
      ...p,
      locationId: p.locationId,
      timeFrame: parseEnumValue(p.timeFrame, isTimeFrame, "DAILY"),
      projectedSales: p.projectedSales.toString(),
      projectedOrders: p.projectedOrders.toString(),
      projectedAverageOrder: p.projectedAverageOrder.toString(),
    })) ?? [];
  const isLoading = !getProjections;
  const error: Error | null = null;
  const isFetching: boolean = false;

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<ProjectionData>();

    return [
      columnHelper.display({
        id: "expander",
        header: "Order Type Breakdown",
        cell: ({ row }) => {
          const isExpanded = expandedRows[row.original.id];
          return row.original.orderTypeBreakdown ? (
            <Button
              className="-ml-2 p-0 hover:bg-transparent"
              onClick={() => handleToggleExpand(row.original.id)}
              variant="ghost"
            >
              {isExpanded ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronRight className="size-4" />
              )}
            </Button>
          ) : null;
        },
        size: 40,
      }),
      columnHelper.accessor("projectionDate", {
        header: "Date",
        cell: ({ getValue }) => formatDate(new Date(getValue()), "MMM D, YYYY"),
        meta: {
          getCellForExport: (d: ProjectionData["projectionDate"]) =>
            new Date(d).toISOString(),
        },
      }),
      columnHelper.accessor("timeFrame", {
        header: "Time Frame",
        filterFn: ({ original }, _, filterValue: string[]) =>
          filterValue.includes(original.timeFrame),
        meta: {
          faceting: {
            title: "Time Frame",
            getOption: (t: ProjectionData["timeFrame"]) => ({
              value: t,
              label:
                timeFrameOptions.find((opt) => opt.value === t)?.label ?? t,
            }),
          },
          onFilterChange: (values: string[]) => {
            const next = values[0];
            if (values.length === 1 && next && isTimeFrame(next)) {
              setTimeFrame(next);
            }
          },
        },
      }),
      columnHelper.accessor("projectedSales", {
        header: "Projected Sales",
        cell: ({ getValue }) =>
          Number.parseFloat(getValue()).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
        meta: {
          getCellForExport: (d: ProjectionData["projectedSales"]) =>
            Number.parseFloat(d),
        },
      }),
      columnHelper.accessor("projectedOrders", {
        header: "Projected Orders",
        cell: ({ getValue }) =>
          Number.parseFloat(getValue()).toLocaleString("en-US"),
        meta: {
          getCellForExport: (d: ProjectionData["projectedOrders"]) =>
            Number.parseFloat(d),
        },
      }),
      columnHelper.accessor("projectedAverageOrder", {
        header: "Projected Average Order",
        cell: ({ getValue }) =>
          Number.parseFloat(getValue()).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
        meta: {
          getCellForExport: (d: ProjectionData["projectedAverageOrder"]) =>
            Number.parseFloat(d),
        },
      }),
    ] as ColumnDef<ProjectionData>[];
  }, [expandedRows, handleToggleExpand]);

  if (error && !isLoading && !isFetching) {
    return (
      <div className="border-destructive/20 bg-destructive/10 rounded-lg border p-4">
        <p className="text-destructive">Error loading projections data</p>
        <button
          className="bg-destructive/20 text-destructive hover:bg-destructive/30 mt-2 rounded-sm px-4 py-2"
          type="button"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="border-foreground mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-b-2" />
          <p className="body text-muted-foreground">
            Calculating projections...
          </p>
        </div>
      </div>
    );
  }

  if (!projections.length) {
    return (
      <div className="border-border bg-muted rounded-lg border p-4 text-center">
        <p className="body text-foreground">
          No projection data available for the selected time frame
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="relative space-y-2">
        {(isLoading || isFetching) && (
          <div className="bg-background/50 absolute inset-0 z-10 flex items-center justify-center">
            <div className="text-center">
              <div className="border-foreground mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-b-2" />
              <p className="body text-muted-foreground">
                Loading projections...
              </p>
            </div>
          </div>
        )}

        <SalesDataTable
          columns={columns}
          data={projections}
          expandedContent={(row: ProjectionData) =>
            expandedRows[row.id] && row.orderTypeBreakdown ? (
              <tr>
                <td
                  className="border-border border-b p-0"
                  colSpan={columns.length}
                >
                  <div className="bg-muted">
                    <div className="ml-10">
                      <OrderTypeBreakdown breakdown={row.orderTypeBreakdown} />
                    </div>
                  </div>
                </td>
              </tr>
            ) : null
          }
          id={tableId}
        />
      </div>
    </div>
  );
};
